<template>
  <div class="analystic-page">
    <!-- 페이지 헤더 -->
    <PageHeader 
      title="상품 분석" 
      description="등록된 상품들의 조회수 통계를 확인하고 관리할 수 있습니다" 
      :margin-bottom="true"
    />

      <div class="analystic-content">
        <!-- 좌측 트래픽 현황 -->
        <div class="traffic-panel">
          <trafficModal :default-days="30" />
        </div>

        <!-- 우측 필터 + 테이블 -->
        <div class="table-panel">
          <!-- 필터링 옵션 -->
          <div class="filter-row">
            <div class="filter-left">
              <div class="filter-item">
                <el-select v-model="filters.days" size="default" style="width: 100px" @change="loadData">
                  <el-option label="1일" :value="1" />
                  <el-option label="3일" :value="3" />
                  <el-option label="7일" :value="7" />
                  <el-option label="15일" :value="15" />
                  <el-option label="30일" :value="30" />
                  <el-option label="45일" :value="45" />
                  <el-option label="60일" :value="60" />
                  <el-option label="90일" :value="90" />
                </el-select>
              </div>

              <div class="filter-item">
                <el-input
                  v-model="filters.productId"
                  placeholder="상품 ID 검색"
                  size="default"
                  style="width: 150px"
                  clearable
                  @keyup.enter="loadData"
                  @clear="loadData"
                />
              </div>

              <div class="filter-item">
                <label class="filter-label">조회수:</label>
                <el-input-number v-model="filters.minViews" :min="0" placeholder="최소" :controls="false" style="width: 120px;" />
                <span style="margin: 0 5px;">-</span>
                <el-input-number v-model="filters.maxViews" :min="0" placeholder="최대" :controls="false" style="width: 120px;" />
              </div>
            </div>

            <div class="filter-right">
              <el-button type="primary" :icon="Search" @click="loadData" :loading="loading">
                조회
              </el-button>
              <el-tooltip content="조회수가0인 상품은 집계되지 않습니다" placement="top">
                <el-icon class="info-icon"><InfoFilled /></el-icon>
              </el-tooltip>
            </div>
          </div>

          <!-- 데이터 테이블 -->
          <el-table
            ref="tableRef"
            :data="tableData"
            v-loading="loading"
            empty-text="조회된 데이터가 없습니다"
            class="data-table"
          >
              <el-table-column label="상품 정보" min-width="180">
                <template #default="{ row }">
                  <div class="product-info">
                    <div class="product-image">
                      <img 
                        :src="getProductImageUrl(row)" 
                        :alt="row.productName"
                        @error="handleImageError"
                      />
                    </div>
                    <div class="product-details">
                      <div class="product-name">{{ row.productName }}</div>
              <div class="product-meta">
                <span class="product-id">ID: {{ row.productId }}</span>
              </div>
                    </div>
                  </div>
                </template>
              </el-table-column>

              <el-table-column label="전체 조회수" width="100" align="center">
                <template #default="{ row }">
                  <div class="total-views">
                    <div class="total-views-number">{{ getVisibleTotalViews(row) }}</div>
                  </div>
                </template>
              </el-table-column>

              <el-table-column width="110" align="center">
                <template #header>
                  <div class="header-with-count">
                    <span class="product-count">총 {{ tableData.length }}개 상품</span>
                  </div>
                </template>
                <template #default="{ row }">
                  <el-button 
                    type="primary" 
                    size="small"
                    :icon="View"
                    @click="showDetailModal(row)"
                  >
                    상세보기
                  </el-button>
                </template>
              </el-table-column>
          </el-table>
        </div>
      </div>

    <!-- 상세정보 모달 -->
    <viewDetailModal
      :visible="detailModalVisible"
      :product-data="detailData"
      @close="handleCloseDetailModal"
    />

  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { 
  Search, 
  View, 
  InfoFilled
} from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import { getTrackingStats } from '@/services/manager';
import viewDetailModal from '@/components/modal/viewDetailModal.vue';
import trafficModal from '@/components/modal/trafficModal.vue';
import PageHeader from '@/components/common/header.vue';

export default {
  name: 'AnalysticPage',
  components: {
    viewDetailModal,
    trafficModal,
    PageHeader
  },
  setup() {
    // 기본 상태
    const loading = ref(false);
    
    // 필터링
    const filters = ref({
      days: 1,
      productId: '',
      minViews: null,
      maxViews: null
    });

    // 테이블 데이터
    const tableData = ref([]);

    // 상세 모달
    const detailModalVisible = ref(false);
    const detailData = ref(null);
    
    // 데이터 로드
    const loadData = async () => {
      loading.value = true;
      try {
        const params = {
          days: filters.value.days
        };
        
        if (filters.value.productId) {
          params.productId = filters.value.productId;
        }
        
        if (filters.value.minViews !== null && filters.value.minViews !== '') {
          params.minViews = filters.value.minViews;
        }
        
        if (filters.value.maxViews !== null && filters.value.maxViews !== '') {
          params.maxViews = filters.value.maxViews;
        }

        const response = await getTrackingStats(params);
        
        if (response.success) {
          tableData.value = response.data || [];
          ElMessage.success(response.message || '데이터를 성공적으로 불러왔습니다.');
        } else {
          throw new Error(response.message || '데이터 로드 실패');
        }
      } catch (error) {
        console.error('데이터 로드 실패:', error);
        ElMessage.error(error.response?.data?.message || '데이터 로드 중 오류가 발생했습니다.');
        tableData.value = [];
      } finally {
        loading.value = false;
      }
    };

    // 상세 모달 관련
    const showDetailModal = (row) => {
      detailData.value = row;
      detailModalVisible.value = true;
    };

    const handleCloseDetailModal = () => {
      detailModalVisible.value = false;
      detailData.value = null;
    };

    // 유틸리티 함수들
    const getProductImageUrl = (row) => {
      if (!row?.imageUrl || row.productName === '알 수 없는 상품') {
        return '/no-image.jpg';
      }

      return row.imageUrl.startsWith('//') ? `https:${row.imageUrl}` : row.imageUrl;
    };

    const handleImageError = (event) => {
      event.target.onerror = null;
      event.target.src = '/no-image.jpg'; // 기본 이미지로 대체
    };

    const getVisibleTotalViews = (row) => {
      return row?.totalViews || 0;
    };

    onMounted(() => {
      loadData();
    });

    return {
      // 상태
      loading,
      
      // 필터링
      filters,
      
      // 테이블
      tableData,
      
      // 상세 모달
      detailModalVisible,
      detailData,
      
      // 함수들
      loadData,
      showDetailModal,
      handleCloseDetailModal,
      getProductImageUrl,
      handleImageError,
      getVisibleTotalViews,
      
      // 아이콘들
      Search,
      View,
      InfoFilled
    };
  }
};
</script>

<style scoped>
.analystic-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--el-bg-color-page);
}

.analystic-content {
  display: flex;
  gap: var(--spacing-xs);
  flex: 1;
  min-height: 0;
}

.traffic-panel {
  flex: 0 0 800px;
  min-width: 320px;
  background: var(--el-bg-color);
  border-radius: var(--el-border-radius-base);
  border: 1px solid var(--el-border-color-lighter);
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  height: fit-content;
  align-self: flex-start;
}

.table-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* 필터링 */

.filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--el-bg-color);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--el-border-color-lighter);
  margin-bottom: var(--spacing-xs);
}

.filter-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.filter-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.filter-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.filter-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}


/* 테이블 */
.data-table {
  flex: 1;
  overflow: auto;
}

.header-with-count {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
}

.product-count {
  font-size: var(--el-font-size-extra-small);
  color: var(--el-text-color-secondary);
  font-weight: var(--el-font-weight-normal);
}

/* 상품 정보 */
.product-info {
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
}

.product-image img {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: var(--el-border-radius-small);
  border: 1px solid var(--el-border-color-lighter);
}

.product-details {
  flex: 1;
}

.product-name {
  font-weight: var(--el-font-weight-medium);
  color: var(--el-text-color-primary);
  margin-bottom: var(--spacing-xs);
  line-height: 1.4;
}

.product-meta {
  display: flex;
  gap: var(--spacing-sm);
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

/* 플랫폼 카드 (2층 구조) */
.platform-card {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs);
  min-height: 60px;
  justify-content: center;
}

.platform-info-layer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
}

.platform-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.margin-display {
  font-size: var(--el-font-size-small);
  color: var(--el-color-success);
  font-weight: var(--el-font-weight-bold);
  text-align: center;
  line-height: 1.2;
}

.platform-empty {
  color: var(--el-text-color-placeholder);
  font-size: var(--el-font-size-base);
  font-weight: var(--el-font-weight-medium);
}

.views-layer {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: var(--spacing-xs);
  border-top: 1px solid var(--el-border-color-extra-light);
}

.views-display {
  font-size: var(--el-font-size-small);
  color: var(--el-color-primary);
  font-weight: var(--el-font-weight-bold);
  text-align: center;
  line-height: 1.2;
}

.views-only {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-primary);
  font-weight: var(--el-font-weight-bold);
  text-align: center;
}

/* 전체 조회수 */
.total-views {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm);
}

.total-views-number {
  font-size: var(--el-font-size-large);
  font-weight: var(--el-font-weight-bold);
  color: var(--el-color-primary-dark-2);
  line-height: 1;
}

.total-views-label {
  font-size: var(--el-font-size-extra-small);
  color: var(--el-text-color-secondary);
  font-weight: var(--el-font-weight-medium);
}
/* 툴팁 아이콘 */
.info-icon {
  color: var(--el-color-info);
  cursor: pointer;
  margin-left: var(--spacing-xs);
  font-size: var(--el-font-size-base);
  transition: color 0.2s ease;
}

.info-icon:hover {
  color: var(--el-color-info-dark-2);
}



/* 반응형 디자인 */
@media (max-width: 768px) {
  .analystic-content {
    flex-direction: column;
  }

  .traffic-panel {
    width: 100%;
    min-width: 0;
  }

  .filter-row {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm);
  }

  .filter-left,
  .filter-right {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 480px) {
  .page-header {
    padding: var(--spacing-sm);
  }

  .product-info {
    flex-direction: column;
    text-align: center;
  }
  
  .platform-card {
    min-height: 50px;
    padding: var(--spacing-xs);
  }
  
  .margin-display,
  .views-display {
    font-size: var(--el-font-size-extra-small);
  }

  .total-views-number {
    font-size: var(--el-font-size-medium);
  }

  .total-views-label {
    font-size: var(--el-font-size-extra-small);
  }
}
</style>
